<script setup lang="ts">
/**
 * Вкладка «Связи»: строки «блокирует DEV-1443», «связана с DEV-1450» и форма
 * добавления через поиск по ключу и названию.
 *
 * Связь читается с двух концов по-разному: «блокирует» с одной стороны — это
 * «заблокирована» с другой. Сервис помечает такие строки признаком `inbound`,
 * и подпись мы берём по нему — иначе половина связей в трекере врала бы.
 *
 * Родитель правится здесь же: переложить задачу в другую ветку иерархии —
 * действие того же рода, что и связать её с соседней, и жить им нужно рядом.
 */
import { X } from 'lucide-vue-next'
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import InlinePicker from '@/components/InlinePicker.vue'
import { listIssues, parentTypesOf } from '@/features/issues/api'
import IssueTypeIcon from '@/features/issues/components/IssueTypeIcon.vue'
import { emptyFilters } from '@/features/issues/filters'

import { createLink, deleteLink, listLinks, LINK_TYPES, type IssueLink } from '../links'
import { useIssueStore } from '../store'

import type { V1LinkType } from '@/api/clients/tracker-issue'
import type { IssueRow } from '@/features/issues/api'

const emit = defineEmits<{ failed: [error: unknown] }>()

const { t } = useI18n()
const store = useIssueStore()

const links = ref<IssueLink[]>([])
const type = ref<V1LinkType>('LINK_TYPE_BLOCKS')
const query = ref('')
const found = ref<IssueRow[]>([])
const isSearching = ref(false)

const issueId = computed(() => store.issue?.id ?? '')

const typeOptions = computed(() =>
  LINK_TYPES.map((value) => ({ value, label: t(`issue.links.types.${value}`) })),
)

/** Родителем может быть не всякая задача: иерархия в трекере ровно на три уровня */
const parentOptions = ref<IssueRow[]>([])

const load = async () => {
  if (!issueId.value) return

  try {
    links.value = await listLinks(issueId.value)
  } catch (failure) {
    emit('failed', failure)
  }
}

const search = async () => {
  const needle = query.value.trim()

  if (!needle || !store.issue) {
    found.value = []

    return
  }

  isSearching.value = true

  try {
    const page = await listIssues(
      store.issue.projectId,
      { ...emptyFilters(), search: needle },
      { field: 'key', desc: false },
      1,
      10,
    )

    // Саму себя связывать не с чем, и сервис такую связь всё равно отвергнет.
    found.value = page.issues.filter((row) => row.id !== issueId.value)
  } catch (failure) {
    emit('failed', failure)
  } finally {
    isSearching.value = false
  }
}

const link = async (row: IssueRow) => {
  try {
    await createLink(issueId.value, row.id, type.value)

    query.value = ''
    found.value = []
    await load()
  } catch (failure) {
    emit('failed', failure)
  }
}

const unlink = async (entry: IssueLink) => {
  try {
    await deleteLink(entry.id)

    links.value = links.value.filter((row) => row.id !== entry.id)
  } catch (failure) {
    emit('failed', failure)
  }
}

/**
 * Подпись связи зависит от конца: «блокирует» с одной стороны и
 * «заблокирована» с другой — это одна и та же строка в базе.
 */
const caption = (entry: IssueLink) =>
  t(`issue.links.${entry.inbound ? 'inbound' : 'outbound'}.${entry.type || 'LINK_TYPE_RELATES'}`)

const loadParents = async () => {
  const types = parentTypesOf(store.issue?.type ?? '')

  if (!types.length || !store.issue) {
    parentOptions.value = []

    return
  }

  try {
    const page = await listIssues(
      store.issue.projectId,
      { ...emptyFilters(), types },
      { field: 'key', desc: false },
      1,
      50,
    )

    parentOptions.value = page.issues.filter((row) => row.id !== issueId.value)
  } catch {
    // Без списка родителей связи всё равно работают: молчим.
    parentOptions.value = []
  }
}

const parentPicker = computed(() => [
  { value: '', label: t('issues.create.parentPlaceholder') },
  ...parentOptions.value.map((row) => ({ value: row.id, label: `${row.key} · ${row.title}` })),
])

const setParent = (parentId: string) => {
  store.setParent(parentId).catch((failure: unknown) => emit('failed', failure))
}

onMounted(() => {
  void load()
  void loadParents()
})

watch(issueId, () => {
  void load()
  void loadParents()
})
</script>

<template>
  <section class="ilink">
    <div class="ilink__parent">
      <span class="ilink__label">{{ t('issues.create.parent') }}</span>

      <InlinePicker
        :options="parentPicker"
        :model-value="store.issue?.parentId ?? ''"
        :empty-text="t('issues.create.noParents')"
        @update:model-value="setParent"
      >
        <template #trigger="{ toggle }">
          <button type="button" class="ilink__parent-btn" @click="toggle()">
            {{ store.issue?.parentKey || t('issues.create.parentPlaceholder') }}
          </button>
        </template>
      </InlinePicker>
    </div>

    <p v-if="!links.length" class="ilink__empty">{{ t('issue.links.empty') }}</p>

    <div v-for="entry in links" :key="entry.id" class="lrow">
      <span class="lrow__caption">{{ caption(entry) }}</span>

      <RouterLink class="lrow__target" :to="`/issue/${entry.targetKey}`">
        <span class="lrow__key">{{ entry.targetKey }}</span>
        <span class="lrow__title">{{ entry.targetTitle }}</span>
      </RouterLink>

      <button
        type="button"
        class="lrow__off"
        :aria-label="t('issue.links.remove', { key: entry.targetKey })"
        @click="unlink(entry)"
      >
        <X :size="12" />
      </button>
    </div>

    <div class="ilink__add">
      <InlinePicker
        :options="typeOptions"
        :model-value="type"
        @update:model-value="type = $event as V1LinkType"
      >
        <template #trigger="{ toggle }">
          <button type="button" class="ilink__type" @click="toggle()">
            {{ t(`issue.links.types.${type}`) }}
          </button>
        </template>
      </InlinePicker>

      <input
        v-model="query"
        class="ilink__field"
        type="text"
        :placeholder="t('issue.links.search')"
        :aria-label="t('issue.links.search')"
        @input="search"
      />
    </div>

    <div v-if="found.length" class="ilink__found">
      <button v-for="row in found" :key="row.id" type="button" class="ifound" @click="link(row)">
        <IssueTypeIcon :type="row.type" />
        <span class="ifound__key">{{ row.key }}</span>
        <span class="ifound__title">{{ row.title }}</span>
      </button>
    </div>

    <p v-else-if="query.trim() && !isSearching" class="ilink__empty">
      {{ t('board.nothingFound.title') }}
    </p>

  </section>
</template>

<style scoped>
.ilink {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.ilink__parent {
  display: flex;
  align-items: center;
  gap: 8px;
}

.ilink__label {
  color: var(--color-text-muted);
  font-size: var(--font-size-overline);
  font-weight: var(--font-weight-semibold);
  letter-spacing: 0.6px;
  text-transform: uppercase;
}

.ilink__parent-btn {
  padding: 0;
  border: none;
  background: none;
  color: var(--color-primary);
  font-family: var(--font-family-mono);
  font-size: var(--font-size-body-sm);
  cursor: pointer;
}

.ilink__empty {
  margin: 0;
  color: var(--color-text-muted);
  font-size: var(--font-size-body-sm);
}

.lrow {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 29px;
}

.lrow__caption {
  color: var(--color-text-muted);
  font-size: var(--font-size-body-sm);
  white-space: nowrap;
}

.lrow__target {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
  color: inherit;
  text-decoration: none;
}

.lrow__key {
  color: var(--color-text-muted);
  font-family: var(--font-family-mono);
  font-size: var(--font-size-mono);
  line-height: 16px;
}

.lrow__title {
  overflow: hidden;
  color: var(--color-text-primary);
  font-size: var(--font-size-body-sm);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.lrow__target:hover .lrow__title {
  text-decoration: underline;
}

.lrow__off {
  display: inline-flex;
  padding: 0;
  border: none;
  background: none;
  color: var(--color-text-muted);
  cursor: pointer;
  opacity: 0;
}

.lrow:hover .lrow__off,
.lrow__off:focus-visible {
  opacity: 1;
}

.ilink__add {
  display: flex;
  gap: 8px;
}

.ilink__type {
  height: 32px;
  padding: 0 10px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  background: var(--color-surface);
  color: var(--color-text-secondary);
  font-family: var(--font-family-sans);
  font-size: var(--font-size-body-sm);
  white-space: nowrap;
  cursor: pointer;
}

.ilink__field {
  flex: 1;
  height: 32px;
  padding: 0 10px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  background: var(--color-surface);
  color: var(--color-text-primary);
  font-family: var(--font-family-sans);
  font-size: var(--font-size-body-sm);
}

.ilink__found {
  display: flex;
  flex-direction: column;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface);
  overflow: hidden;
}

.ifound {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 32px;
  padding: 0 10px;
  border: none;
  border-top: 1px solid var(--color-border);
  background: none;
  font-family: var(--font-family-sans);
  font-size: var(--font-size-body-sm);
  text-align: left;
  cursor: pointer;
}

.ifound:first-child {
  border-top: none;
}

.ifound:hover {
  background: var(--color-bg-muted);
}

.ifound__key {
  color: var(--color-text-muted);
  font-family: var(--font-family-mono);
  font-size: var(--font-size-mono);
}

.ifound__title {
  overflow: hidden;
  color: var(--color-text-primary);
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
